import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AlertTriangle, Clock, MapPin, Truck } from "react-feather";
import dayjs from "dayjs";
import CustomerPicker from "../../sales/components/CustomerPicker";
import {
  useDispatchAlarmMutation,
  useGetAlarmCustomerSitesQuery,
  useGetAlarmLookupsQuery,
  useGetMobileUnitsQuery,
} from "../alarmsApi";
import { ALARM_STATUS_LABEL, INCIDENT_LABEL } from "../alarmFormat";
import { paths } from "../../../routes/paths";
import { MAROON } from "../../../components/common/brandColors";

const portal = typeof document !== "undefined" ? document.body : undefined;

// The control room's one entry form: a signal came in on the alarm-receiver
// software, the operator records whose alarm it is and what it was, and sends a
// car. Units list only the customer's branch and show which are already busy,
// because sending a car that is mid-response is the mistake this screen must
// make hard.
const DispatchAlarmModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [locationId, setLocationId] = useState("");
  const [incidentType, setIncidentType] = useState("ALARM");
  const [description, setDescription] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [unitGuid, setUnitGuid] = useState("");

  const cust = customer?.customer;
  const { data: lookups } = useGetAlarmLookupsQuery(undefined, { skip: !show });
  const { data: sites = [], isFetching: loadingSites } = useGetAlarmCustomerSitesQuery(
    { branchId: cust?.branchId, custId: cust?.custId },
    { skip: !cust }
  );
  const { data: units = [], isFetching: loadingUnits } = useGetMobileUnitsQuery(
    { branchId: cust?.branchId },
    { skip: !cust, pollingInterval: show ? 30000 : 0 }
  );
  const [dispatchAlarm, { isLoading: saving }] = useDispatchAlarmMutation();

  useEffect(() => {
    if (!show) return;
    setCustomer(null);
    setLocationId("");
    setIncidentType("ALARM");
    setDescription("");
    setUnitGuid("");
  }, [show]);

  // The signal-time display ticks every second while the form is open.
  useEffect(() => {
    if (!show) return undefined;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [show]);

  // New customer: default to its primary site, and forget a unit picked for another branch.
  useEffect(() => {
    const primary = sites.find((s) => s.isPrimary) || sites[0];
    setLocationId(primary ? String(primary.locationId) : "");
  }, [sites]);
  useEffect(() => setUnitGuid(""), [cust?.branchId]);

  const site = sites.find((s) => String(s.locationId) === locationId);
  const unit = units.find((u) => u.recordGuid === unitGuid);
  const incidentTypes = lookups?.incidentTypes || Object.entries(INCIDENT_LABEL).map(([code, label]) => ({ code, label }));

  // Free units first, then busy ones; units without a device login cannot receive anything.
  const sortedUnits = useMemo(
    () => [...units].sort((a, b) => Number(!!a.activeResponseNumber) - Number(!!b.activeResponseNumber) || a.unitName.localeCompare(b.unitName)),
    [units]
  );

  const canSubmit = cust && incidentType && description.trim() && unit && unit.deviceUserId && !saving;

  const submit = async () => {
    try {
      const detail = await dispatchAlarm({
        branchId: cust.branchId,
        custId: cust.custId,
        locationId: locationId ? Number(locationId) : null,
        incidentType,
        incidentDescription: description.trim(),
        // No signal time is sent: the server stamps the moment of dispatch.
        signalReceivedAt: null,
        unitRecordGuid: unitGuid,
      }).unwrap();
      toast.success(`${detail.response.responseNumber} sent to ${detail.response.unitName}`);
      onClose();
      navigate(paths.alarmResponseDetail(detail.response.recordGuid));
    } catch (e) {
      toast.error(e?.message || "Could not dispatch the alarm");
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <AlertTriangle size={20} color="#C0392B" /> New Alarm
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">Customer <span className="text-danger">*</span></label>
          <CustomerPicker value={customer} onChange={setCustomer} menuPortalTarget={portal} isClearable />
          {cust && (
            <small className="text-muted d-block mt-1">
              {cust.branchCode}
              {cust.contactPerson ? ` · ${cust.contactPerson}` : ""}
            </small>
          )}
        </div>

        {cust && (
          <div className="mb-3">
            <label className="form-label d-flex align-items-center gap-1">
              <MapPin size={14} /> Site
            </label>
            {loadingSites ? (
              <div className="text-muted small">Loading sites...</div>
            ) : sites.length === 0 ? (
              <div className="alert alert-warning py-2 mb-0 small">
                This customer has no site recorded. The unit will only have the customer&apos;s address to go by.
              </div>
            ) : (
              <>
                <select className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  {sites.map((s) => (
                    <option key={s.locationId} value={s.locationId}>
                      {s.siteLabel}
                      {s.isPrimary ? " (primary)" : ""}
                      {s.address ? ` - ${s.address}` : ""}
                    </option>
                  ))}
                </select>
                {site && site.latitude == null && (
                  <small className="text-warning d-block mt-1">
                    No GPS pin saved for this site - the unit will navigate by address.
                  </small>
                )}
              </>
            )}
          </div>
        )}

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label">Incident <span className="text-danger">*</span></label>
            <select className="form-select" value={incidentType} onChange={(e) => setIncidentType(e.target.value)}>
              {incidentTypes.map((t) => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label d-flex align-items-center gap-1">
              <Clock size={14} /> Signal time
            </label>
            {/* Live, not editable: the operator records the signal as it comes in and
                the server stamps that moment when the alarm is dispatched. */}
            <div className="form-control bg-light fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
              {dayjs(now).format("DD MMM YYYY, h:mm:ss A")}
            </div>
            <small className="text-muted">Recorded automatically when you dispatch.</small>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">What happened <span className="text-danger">*</span></label>
          <textarea
            className="form-control"
            rows={2}
            maxLength={500}
            value={description}
            placeholder="e.g. Zone 3 PIR, rear door - no answer on the customer's phone"
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="mb-1">
          <label className="form-label d-flex align-items-center gap-1">
            <Truck size={14} /> Mobile unit <span className="text-danger">*</span>
          </label>
          {!cust ? (
            <div className="text-muted small">Pick the customer first - units are listed for the customer&apos;s branch.</div>
          ) : loadingUnits && units.length === 0 ? (
            <div className="text-muted small">Loading units...</div>
          ) : units.length === 0 ? (
            <div className="alert alert-warning py-2 mb-0 small">No active mobile units in {cust.branchCode}.</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {sortedUnits.map((u) => {
                const busy = !!u.activeResponseNumber;
                const noDevice = !u.deviceUserId;
                return (
                  <label
                    key={u.recordGuid}
                    className={`d-flex align-items-center gap-2 border rounded px-3 py-2 ${noDevice ? "opacity-50" : ""}`}
                    style={{ cursor: noDevice ? "not-allowed" : "pointer", borderColor: unitGuid === u.recordGuid ? MAROON : undefined }}
                  >
                    <input
                      type="radio"
                      name="unit"
                      disabled={noDevice}
                      checked={unitGuid === u.recordGuid}
                      onChange={() => setUnitGuid(u.recordGuid)}
                    />
                    <span className="fw-semibold">{u.unitName}</span>
                    {u.vehicleRegNo && <span className="text-muted small">{u.vehicleRegNo}</span>}
                    <span className="ms-auto small">
                      {noDevice ? (
                        <span className="text-danger">No device login</span>
                      ) : busy ? (
                        <span className="text-warning fw-semibold">
                          Busy - {u.activeResponseNumber} ({ALARM_STATUS_LABEL[u.activeResponseStatus] || u.activeResponseStatus})
                        </span>
                      ) : (
                        <span className="text-success fw-semibold">Free</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="danger" onClick={submit} disabled={!canSubmit}>
          {saving ? "Dispatching..." : "Dispatch"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DispatchAlarmModal;
